import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import Iconify from 'src/components/Iconify';
import { listChatbots, createChatbot, deleteChatbot, duplicateChatbot } from 'src/actions/v1/chatbots/v1_actionChatbots';
import useAuth from 'src/hooks/useAuth';
import ModalConfimacao from 'src/components/modal/ModalConfimacao';

// ----------------------------------------------------------------------

export default function ChatbotList() {
    const { user } = useAuth();
    const [chatbots, setChatbots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [chatbotIdToDelete, setChatbotIdToDelete] = useState(null);
    const [openModalConfirmacao, setOpenModalConfirmacao] = useState(false);
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const fetchChatbots = async () => {
        try {
            setLoading(true);
            const response = await listChatbots(user.clienteId);
            setChatbots(response.data.data);
        } catch (error) {
            enqueueSnackbar('Erro ao buscar chatbots', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.clienteId) {
            fetchChatbots();
        }
    }, [user]);

    const handleCreate = async () => {
        try {
            const newBot = {
                name: 'Novo Chatbot',
                description: 'Descrição do chatbot',
                clienteId: user.clienteId,
                flowData: { nodes: [], edges: [] },
            };
            const response = await createChatbot(newBot);
            setChatbots([...chatbots, response.data.data]);
            enqueueSnackbar('Chatbot criado com sucesso', { variant: 'success' });
            navigate(`/dashboard/chatbots/builder/${response.data.data._id}`);
        } catch (error) {
            enqueueSnackbar('Erro ao criar chatbot', { variant: 'error' });
        }
    };

    const handleDuplicate = async (id) => {
        try {
            const response = await duplicateChatbot(id);
            setChatbots([...chatbots, response.data.data]);
            enqueueSnackbar('Chatbot duplicado com sucesso', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar('Erro ao duplicar chatbot', { variant: 'error' });
        }
    };

    const handleDelete = async () => {
        if (!chatbotIdToDelete) return;
        try {
            await deleteChatbot(chatbotIdToDelete);
            setChatbots(chatbots.filter((c) => c._id !== chatbotIdToDelete));
            enqueueSnackbar('Chatbot removido com sucesso', { variant: 'success' });
            setOpenModalConfirmacao(false);
            setChatbotIdToDelete(null);
        } catch (error) {
            enqueueSnackbar('Erro ao remover chatbot', { variant: 'error' });
        }
    };

    return (
        <div className="flex flex-col h-full w-full gap-6 p-2 md:p-0 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <ModalConfimacao
                open={openModalConfirmacao}
                onClose={() => {
                    setChatbotIdToDelete(null);
                    setOpenModalConfirmacao(false);
                }}
                mensagem="Deseja mesmo excluir este chatbot?"
                onConfirm={handleDelete}
            />

            {/* Header - Design System */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Chatbots</h1>
                    <p className="text-sm text-gray-500 mt-1">Crie e gerencie seus fluxos de chatbot personalizados.</p>
                </div>

                {/* Action Button */}
                <button
                    onClick={handleCreate}
                    className="flex items-center gap-2 px-4 py-2.5 bg-[#7C3AED] hover:bg-[#6d28d9] text-white text-sm font-medium rounded-xl transition-all shadow-sm shadow-[#7C3AED]/20 active:transform active:scale-95"
                >
                    <Iconify icon="solar:add-circle-bold" width={20} height={20} />
                    <span>Novo Chatbot</span>
                </button>
            </div>

            {/* Content Grid */}
            <div className="mt-4">
                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
                        {Array(4)
                            .fill(0)
                            .map((_, index) => (
                                <SkeletonChatbotCard key={index} />
                            ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
                        {/* Create New Card - Always First */}
                        <CardNew onClick={handleCreate} />
                        {/* Chatbot List */}
                        {chatbots &&
                            chatbots.map((bot, index) => (
                                <ChatbotCard
                                    key={bot._id || index}
                                    chatbot={bot}
                                    onClick={() => navigate(`/dashboard/chatbots/builder/${bot._id}`)}
                                    onDuplicate={() => handleDuplicate(bot._id)}
                                    onDelete={() => {
                                        setChatbotIdToDelete(bot._id);
                                        setOpenModalConfirmacao(true);
                                    }}
                                />
                            ))}
                    </div>
                )}
            </div>
        </div>
    );
}

// ----------------------------------------------------------------------

function ChatbotCard({ chatbot, onClick, onDelete, onDuplicate }) {
    const hasNodes = chatbot.flowData?.nodes?.length > 0;

    return (
        <div
            onClick={onClick}
            className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 relative flex flex-col h-full min-h-[240px] hover:shadow-md hover:border-[#7C3AED]/30 transition-all duration-300 animate-in fade-in slide-in-from-bottom-2 duration-300 cursor-pointer group"
        >
            {/* Header Actions (Status & Delete) */}
            <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
                {/* Status Dot */}
                <div
                    className={`w-2.5 h-2.5 rounded-full shadow-sm ring-2 ring-white ${hasNodes ? 'bg-green-500' : 'bg-gray-300'}`}
                    title={hasNodes ? 'Ativo' : 'Vazio'}
                />
                {/* Duplicate Button - Visible on Hover */}
                <button
                    className="p-1.5 rounded-lg text-gray-400 hover:text-[#7C3AED] hover:bg-violet-50 transition-all opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto"
                    onClick={(e) => {
                        e.stopPropagation();
                        onDuplicate();
                    }}
                    title="Duplicar chatbot"
                >
                    <Iconify icon="solar:copy-bold" width={18} height={18} />
                </button>
                {/* Delete Button - Visible on Hover */}
                <button
                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto"
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                    }}
                    title="Excluir chatbot"
                >
                    <Iconify icon="solar:trash-bin-trash-bold" width={18} height={18} />
                </button>
            </div>

            {/* Content */}
            <div className="flex-1 flex flex-col items-center pt-2">
                {/* Icon with subtle background */}
                <div
                    className={`mb-3 p-3 rounded-lg group-hover:scale-105 transition-transform duration-300 ${hasNodes ? 'bg-[#7C3AED]/10 text-[#7C3AED]' : 'bg-gray-100 text-gray-500'
                        }`}
                >
                    <Iconify icon="solar:chat-round-dots-bold" width={28} height={28} />
                </div>

                <h3 className="text-base font-semibold text-gray-900 text-center line-clamp-1 mb-1 px-2" title={chatbot.name}>
                    {chatbot.name}
                </h3>

                <p
                    className="text-[11px] text-gray-500 mb-4 bg-gray-50 px-2.5 py-0.5 rounded-full font-medium tracking-wide line-clamp-1 max-w-full"
                    title={chatbot.description}
                >
                    {chatbot.description || 'Sem descrição'}
                </p>

                {/* Fill space */}
                <div className="flex-1 w-full flex flex-col justify-end gap-3 mt-2">
                    {/* Divider */}
                    <div className="h-px bg-gradient-to-r from-transparent via-gray-100 to-transparent w-full" />

                    {/* Nodes Info */}
                    {hasNodes ? (
                        <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                            <Iconify icon="solar:diagram-up-bold" width={14} />
                            <span>{chatbot.flowData?.nodes?.length || 0} nós no fluxo</span>
                        </div>
                    ) : (
                        <div className="flex items-center justify-center gap-2 text-xs text-amber-600 bg-amber-50 py-1 rounded-md">
                            <Iconify icon="solar:pen-bold" width={12} />
                            <span>Fluxo vazio</span>
                        </div>
                    )}

                    {/* Config Button Area */}
                    <div className="grid grid-cols-1 w-full">
                        <button className="flex-1 flex items-center justify-center gap-1.5 bg-gray-50 text-gray-700 px-3 py-2 rounded-lg text-xs font-semibold hover:bg-gray-100 hover:text-gray-900 transition-all border border-transparent hover:border-gray-200">
                            <Iconify icon={hasNodes ? 'solar:settings-bold' : 'solar:pen-new-square-bold'} width={16} height={16} />
                            <span>{hasNodes ? 'Configurar' : 'Editar Fluxo'}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ----------------------------------------------------------------------

const CardNew = ({ onClick }) => {
    return (
        <button
            onClick={onClick}
            className="group relative flex flex-col items-center justify-center p-6 rounded-xl border-2 border-dashed transition-all duration-300 min-h-[240px] w-full animate-in fade-in slide-in-from-bottom-2 duration-300 border-[#7C3AED]/30 bg-[#7C3AED]/5 hover:bg-[#7C3AED]/10 hover:border-[#7C3AED]/50 cursor-pointer"
        >
            <div className="w-14 h-14 rounded-lg flex items-center justify-center mb-3 transition-all duration-300 bg-white text-[#7C3AED] shadow-sm group-hover:scale-110 group-hover:shadow-md">
                <Iconify icon="solar:add-circle-bold" width={32} height={32} />
            </div>
            <h3 className="text-base font-semibold mb-1 text-[#7C3AED]">Novo Chatbot</h3>
            <p className="text-sm text-center px-4 text-gray-500 leading-relaxed">Crie um novo fluxo de conversação</p>
        </button>
    );
};

// ----------------------------------------------------------------------

function SkeletonChatbotCard() {
    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 relative overflow-hidden h-[240px]">
            <div className="flex flex-col space-y-6 h-full items-center justify-center">
                <div className="flex flex-col items-center justify-center space-y-4 w-full">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg animate-pulse"></div>
                    <div className="w-32 h-4 bg-gray-100 rounded animate-pulse"></div>
                </div>
                <div className="w-full border-t border-dashed border-gray-100"></div>
                <div className="flex flex-col items-center space-y-3 w-full">
                    <div className="w-24 h-6 bg-gray-100 rounded-lg animate-pulse"></div>
                    <div className="w-full h-8 bg-gray-50 rounded-lg animate-pulse"></div>
                </div>
            </div>
        </div>
    );
}